import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { 
    TextField, 
    Button, 
    Typography, 
    CircularProgress,
    Paper,
    Box,
    Stack
} from '@mui/material';
import { format, subYears, differenceInYears } from 'date-fns';
import { createPerson } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PersonForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize toast
  const notifySuccess = () => toast.success('Created successfully!');
  const notifyError = () => toast.error('An error occurred. Please try again.');

  const validationSchema = Yup.object({
    full_name: Yup.string().required('Full name is required'),
    birthdate: Yup.date()
      .required('Birthdate is required')
      .max(subYears(new Date(), 1), 'Birthdate must be in the past'),
    age: Yup.number()
    .required('Age is required')
    .min(1, 'Age must be greater than 0')
    .integer('Age must be a whole number'),
    description: Yup.string().required('Description is required'),
  });

  const formik = useFormik({
    initialValues: {
      full_name: '',
      birthdate: '',
      age: '',
      description: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        await createPerson({
          full_name: values.full_name,
          birthdate: values.birthdate,
          age: values.age,
          description: values.description
        });
        
        notifySuccess();
        resetForm(); // Clear all form fields
      } catch (error) {
        console.error('Error:', error);
        notifyError();
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Profiling
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        {/* First Row - Full Name, Birthdate, Age */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            id="full_name"
            name="full_name"
            label="Full Name"
            value={formik.values.full_name}
            onChange={formik.handleChange}
            error={formik.touched.full_name && Boolean(formik.errors.full_name)}
            helperText={formik.touched.full_name && formik.errors.full_name}
          />
          
          <TextField
            fullWidth
            id="birthdate"
            name="birthdate"
            label="Birthdate"
            value={formik.values.birthdate}
            onChange={formik.handleChange}
            error={formik.touched.birthdate && Boolean(formik.errors.birthdate)}
            helperText={formik.touched.birthdate && formik.errors.birthdate}
          />
          
          <TextField
            fullWidth
            id="age"
            name="age"
            label="Age"
            type="number"
            inputProps={{ min: 1 }}
            value={formik.values.age}
            onChange={formik.handleChange}
            error={formik.touched.age && Boolean(formik.errors.age)}
            helperText={formik.touched.age && formik.errors.age}
          />
        </Stack>
        
        {/* Second Row - Description (full width) */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            multiline
            rows={4}
            value={formik.values.description}
            onChange={formik.handleChange}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
          />
        </Box>
        
        {/* Submit Button - Right aligned */}
        <Box display="flex" justifyContent="flex-end">
          <Button 
            color="primary" 
            variant="contained" 
            type="submit"
            disabled={isSubmitting}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Add Profile'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default PersonForm;